import { useState } from 'react'
import { ClipboardList } from 'lucide-react'
import clsx from 'clsx'
import { formatRelative } from '@/utils/format'
import { isAssessed } from '@/utils/patchStage'
import { RiskBadge } from './PatchBadges'
import type { MissingCounts, PatchStage, VmAssessment, VmRecovery, VmRisk } from '@/types/patching'
import styles from './Patching.module.css'

interface Row {
  name: string
  assessment?: VmAssessment
  risk?: VmRisk
}

/** Join assessments and risk decisions by VM name; a VM with only one of the two still gets a row. */
function buildRows(stage: PatchStage): Row[] {
  const byName = new Map<string, Row>()
  for (const assessment of stage.assessments) {
    byName.set(assessment.name.toLowerCase(), { name: assessment.name, assessment })
  }
  for (const risk of stage.risks) {
    const key = risk.vm_name.toLowerCase()
    const existing = byName.get(key)
    byName.set(key, existing ? { ...existing, risk } : { name: risk.vm_name, risk })
  }
  return [...byName.values()]
}

function patchGroup(classification: string): 'critical' | 'security' | 'other' {
  const key = classification.toLowerCase()
  if (key.includes('critical')) return 'critical'
  if (key.includes('security')) return 'security'
  return 'other'
}

function MissingCell({
  missing,
  open,
  onOpen,
}: {
  missing: MissingCounts
  open: string | null
  onOpen: (group: string) => void
}) {
  const items: [string, number, string][] = [
    ['C', missing.critical, 'critical'],
    ['S', missing.security, 'security'],
    ['O', missing.other, 'other'],
  ]
  return (
    <span className={styles.counts}>
      {items.map(([short, n, long]) => (
        <button
          key={short}
          type="button"
          className={clsx(styles.count, n > 0 && styles.countHot, open === long && styles.countOpen)}
          title={`${n} ${long} update${n === 1 ? '' : 's'} missing`}
          aria-expanded={open === long}
          onClick={() => onOpen(long)}
        >
          {short} {n}
        </button>
      ))}
    </span>
  )
}

/** Contract §11.7: a VM that could not be assessed has no counts — say so, never show zeros. */
function NotAssessed() {
  return (
    <span>
      <span
        className={clsx(styles.badge, styles.compUnverified)}
        data-testid="not-assessed"
        title="The VM was powered off or its assessment failed, so there are no update counts. It is never treated as having nothing missing."
      >
        Not assessed
      </span>
      <span className={styles.cellSub}>powered off or assessment failed</span>
    </span>
  )
}

function RebootCell({ assessment }: { assessment: VmAssessment }) {
  if (!isAssessed(assessment) || assessment.reboot_pending === null || assessment.reboot_pending === undefined) {
    return (
      <span data-testid="reboot-unknown" aria-label="Reboot pending: Unknown" title="Reboot pending: Unknown (not assessed)">
        Unknown
      </span>
    )
  }
  return <span data-testid="reboot-known">{assessment.reboot_pending ? 'Yes' : 'No'}</span>
}

function RecoveryCell({ recovery }: { recovery: VmRecovery | null | undefined }) {
  // null/absent means "not checked / unavailable" — it is never presented as healthy.
  if (recovery === null || recovery === undefined) {
    return (
      <span data-testid="recovery-unknown" className={clsx(styles.badge, styles.compUnverified)} title="Recovery readiness was not checked or is unavailable; it is never assumed healthy.">
        Not checked
      </span>
    )
  }
  if (!recovery.protected) {
    return <span data-testid="recovery-unprotected" className={clsx(styles.badge, styles.badgeFailed)}>Not protected</span>
  }
  return (
    <span data-testid="recovery-protected">
      Protected
      <span className={styles.cellSub}>
        {recovery.last_recovery_point ? `last point ${formatRelative(recovery.last_recovery_point)}` : 'no recovery point recorded'}
        {recovery.age_hours !== null && recovery.age_hours !== undefined ? ` (${recovery.age_hours} h old)` : ''}
      </span>
    </span>
  )
}

function AssessmentRow({ row }: { row: Row }) {
  const { name, assessment, risk } = row
  const [open, setOpen] = useState<string | null>(null)
  const patches = (assessment?.patches ?? []).filter((patch) => open !== null && patchGroup(patch.classification) === open)
  return (
    <>
      <tr
        className={clsx(risk?.blocked && styles.rowBlocked)}
        data-vm={name}
        data-blocked={risk?.blocked ? 'true' : 'false'}
        data-assessed={assessment ? (isAssessed(assessment) ? 'true' : 'false') : 'unknown'}
      >
        <td>
          <strong>{name}</strong>
          {assessment && (
            <span className={styles.cellSub}>
              {assessment.os_type}
              {assessment.power_state ? ` · ${assessment.power_state}` : ''}
              {assessment.patch_mode ? ` · ${assessment.patch_mode}` : ''}
            </span>
          )}
        </td>
        <td>
          {assessment ? (
            isAssessed(assessment) && assessment.missing ? (
              <MissingCell missing={assessment.missing} open={open} onOpen={(group) => setOpen(open === group ? null : group)} />
            ) : (
              <NotAssessed />
            )
          ) : (
            '—'
          )}
        </td>
        <td>{assessment ? <RebootCell assessment={assessment} /> : '—'}</td>
        <td>
          {assessment && isAssessed(assessment) ? (
            <>
              <span
                className={clsx(styles.badge, assessment.assessment_fresh ? styles.badgeDone : styles.compUnverified)}
                data-testid="assessment-freshness"
                data-fresh={assessment.assessment_fresh ? 'true' : 'false'}
              >
                {assessment.assessment_fresh ? 'Fresh' : 'Stale'}
              </span>
              <span className={styles.cellSub}>
                {assessment.last_assessed ? formatRelative(assessment.last_assessed) : 'never assessed'}
              </span>
            </>
          ) : (
            '—'
          )}
        </td>
        <td>{assessment ? <RecoveryCell recovery={assessment.recovery} /> : '—'}</td>
        <td>
          {risk ? (
            <>
              <RiskBadge risk={risk} />
              {risk.reasons.length > 0 && (
                <ul className={styles.reasons} data-testid="risk-reasons">
                  {risk.reasons.map((reason, i) => (
                    <li key={`${i}-${reason}`}>{reason}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <span className={styles.muted}>Not evaluated</span>
          )}
        </td>
      </tr>
      {open && (
        <tr data-vm={`${name}-patches`}>
          <td colSpan={6}>
            <PatchDetail group={open} patches={patches} />
          </td>
        </tr>
      )}
    </>
  )
}

function PatchDetail({
  group,
  patches,
}: {
  group: string
  patches: { name: string; kb_or_package: string; classification: string; reboot?: string; state?: string }[]
}) {
  return (
    <div data-testid="patch-detail">
      <strong>{group[0].toUpperCase()}{group.slice(1)} updates</strong>
      {patches.length === 0 ? (
        <p className={styles.muted}>Azure did not return the individual updates for this count.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Patch number</th>
              <th>Detail</th>
              <th>Class</th>
              <th>Install</th>
            </tr>
          </thead>
          <tbody>
            {patches.map((patch) => (
              <tr key={`${patch.kb_or_package}-${patch.name}`}>
                <td className={styles.mono}>{patch.kb_or_package || '—'}</td>
                <td>
                  {patch.name}
                  {patch.reboot ? <span className={styles.cellSub}>Reboot: {patch.reboot}</span> : null}
                </td>
                <td>{patch.classification}</td>
                <td>
                  {patch.state === 'not selected'
                    ? 'Not selected for this install'
                    : patch.state === 'installed'
                      ? 'Installed'
                      : patch.state === 'in progress'
                        ? 'In progress. Azure has not named which update is still pending.'
                        : 'Included in this install'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function AssessmentTable({ stage }: { stage: PatchStage }) {
  const rows = buildRows(stage)
  return (
    <section className={styles.section} aria-label="Assessment and risk" data-testid="assessment-panel">
      <h3 className={styles.sectionTitle}>
        <ClipboardList size={14} /> Assessment &amp; risk
      </h3>
      {rows.length === 0 ? (
        <p className={styles.muted}>No assessment has been reported for this environment yet.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>VM</th>
                <th>Missing updates</th>
                <th>Reboot pending</th>
                <th>Assessment</th>
                <th>Recovery / backup</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <AssessmentRow key={row.name} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {stage.assessment_limitations.length > 0 && (
        <div className={styles.legend} data-testid="assessment-limitations">
          <strong>Assessment limitations</strong>
          <ul className={styles.advisorList}>
            {stage.assessment_limitations.map((limitation, i) => (
              <li key={`${i}-${limitation}`}>{limitation}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
